import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { ProductionJob, ProductionJobStatus } from '../../../models/review/production-job.model';
import { ProductionValidationResponse } from '../../../models/production-validation-response.model';
import { ProductionValidationView, ProductionValidator, ProductionValidatorType } from '../../../models/production-validation-view.model';
import { ProductionValidationService } from '../../../services/production-validation.service';

@Component({
    selector: 'lex-production-validation',
    templateUrl: './production-validation.component.html',
    styleUrls: ['./production-validation.component.scss'],
    standalone: false
})
export class ProductionValidationComponent implements OnInit, OnDestroy {

	@Input() productionJob: ProductionJob;
	@Output() validationCompleteEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

	get ValidationType(): typeof ProductionValidatorType { return ProductionValidatorType; }

	private validationManager$: Subject<ProductionValidationView>;
	validators: ProductionValidationView[] = [
		{ id: 0, displayName: 'Partial Email Family', validator: ProductionValidator.EmailFamilies, content: '<b>WARNING:</b> Some documents have email family members that are not in this production. Click Accept to acknowledge and continue validation.', initiated: false, type: ProductionValidatorType.Warning },
		{ id: 1, displayName: 'Privileged: Needs Redaction Withheld Status', validator: ProductionValidator.RedactForPrivilegeWithheldStatus, content: '<b>ERROR:</b> Some documents are marked Privileged: Needs Redaction but are not marked Attorney-Client Privilege or Attorney-Work Product. The production cannot be validated. Please contact Professional Services for assistance.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 2, displayName: 'Privileged: Needs Redaction Redaction Status', validator: ProductionValidator.RedactForPrivilegeRedactionStatus, content: '<b>ERROR:</b> Some documents are marked Privileged: Needs Redaction but are missing redactions. The production cannot be validated. Please contact Professional Services for assistance.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 3, displayName: 'Personal Information: Needs Redaction Redaction Status', validator: ProductionValidator.RedactForPiRedactionStatus, content: '<b>ERROR:</b> Some documents are marked Personal Information: Needs Redaction but are missing redactions. The production cannot be validated. Please contact Professional Services for assistance.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 4, displayName: 'Redacted Pdf', validator: ProductionValidator.RedactedPdf, content: '<b>ERROR:</b> Some documents are marked Redacted but are missing the redacted PDF file. The production cannot be validated. Please contact Professional Services for assistance.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 5, displayName: 'Redacted Native', validator: ProductionValidator.RedactedNative, content: '<b>ERROR:</b> Some documents are marked with Native Redactions but are missing the redacted Native file. The production cannot be validated. Please contact Professional Services for assistance.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 6, displayName: 'Document Responsiveness', validator: ProductionValidator.ResponsiveInvalid, content: '<b>ERROR:</b> Some documents in this production are coded as Not Specified or Needs Further Review.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 7, displayName: 'Non Email Responsiveness', validator: ProductionValidator.ResponsiveNotResponsiveNative, content: '<b>ERROR:</b> Some documents are marked as Non-Responsive.', initiated: false, type: ProductionValidatorType.Error },
		{ id: 8, displayName: 'Email Family Responsiveness', validator: ProductionValidator.ResponsiveEmailFamily, content: '<b>WARNING:</b> Some documents have inconsistent coding within the email family of the Responsive tag. Click Accept to acknowledge and continue validation.', initiated: false, type: ProductionValidatorType.Warning },
		{ id: 9, displayName: 'Email Family Attorney-Client Privilege', validator: ProductionValidator.PrivilegeEmailFamily, content: '<b>WARNING:</b> Some documents have inconsistent coding within the email family of the Privilege tag. Click Accept to acknowledge and continue validation.', initiated: false, type: ProductionValidatorType.Warning },
		{ id: 10, displayName: 'Email Family Attorney-Work Product', validator: ProductionValidator.WorkProductEmailFamily, content: '<b>WARNING:</b> Some documents have inconsistent coding within the email family of the Work Product tag. Click Accept to acknowledge and continue validation.', initiated: false, type: ProductionValidatorType.Warning },
		{ id: 11, displayName: 'Document Page Count', validator: ProductionValidator.PageCount, content: '<b>WARNING:</b> Some documents are over 500 pages. Large documents may require long processing times and may not finish within a day. Users should attempt to separate documents larger than 500 pages before producing. Click Accept to acknowledge and continue validation.', initiated: false, type: ProductionValidatorType.Warning }];

	public step = -1;
	public expandedId: number;
	public triggeredValidationCount: number;
	public valiating: boolean = false;
	productionJobRunStatus = ProductionJobStatus;
	waitTimeMs: number = 1000;

	constructor(private productionValidtionService: ProductionValidationService,
		private activatedRoute: ActivatedRoute,
		private router: Router) { }

	ngOnInit(): void {
		this.validationManager$ = new Subject<ProductionValidationView>();
		this.validationManager$.subscribe(data => { this.handleValidation(data) })
		this.initiate();
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: { v: '1' },
			queryParamsHandling: 'merge'
		});
	}

	initiate() {
		this.step = 0;
		this.valiating = true;
		this.validationManager$.next(this.validators.find(v => v.id === this.step));
	}

	ngOnDestroy(): void {
		this.validationManager$.unsubscribe();

	}

	handleValidation(validator: ProductionValidationView) {
		if (!validator) {
			this.step = this.validators.length;
			return;
		}
		validator.initiated = true;
		const timeStart = new Date().getTime();
		this.productionValidtionService.runValidationCheck(this.productionJob.productionJobId, validator.validator).subscribe(async (response: ProductionValidationResponse) => {
			const timeEnd = new Date().getTime();
			const timeElapsed = timeEnd - timeStart;
			if (timeElapsed < this.waitTimeMs) {
				const waitTime = this.waitTimeMs - timeElapsed;
				await this.timeout(waitTime);
			}
			this.valiating = false;
			this.checkResponse(response);
		});
	}

	async timeout(ms) {
		return new Promise(resovle => setTimeout(resovle, ms));
	}

	checkResponse(r: ProductionValidationResponse) {
		if (r.valid)
			this.nextStep();
		else {
			this.expandedId = this.validators.find(v => v.validator === r.validator).id;
			this.triggeredValidationCount = r.count;
		}
	}

	nextStep() {
		this.expandedId = null;
		this.triggeredValidationCount = null;
		this.step++;
		if (this.step > this.validators.length) {
			this.router.navigate([], {
				relativeTo: this.activatedRoute,
				queryParams: { ProductionJobId: this.productionJob.productionJobId }
			});
			this.validationCompleteEvent.emit(true);
		}
		this.valiating = true;
		this.validationManager$.next(this.validators.find(v => v.id === this.step));
	}

	goToBrowse(validation: ProductionValidationView) {
		this.router.navigate(['review/browse'], { queryParams: { ProductionValidator: validation.validator, ProductionValidationJobId: this.productionJob.productionJobId } });
	}

	validationActions(validation: ProductionValidationView) {
		this.productionValidtionService.updateActions(this.productionJob.productionJobId, validation.validator).subscribe(r => {
			if (r)
				this.validationManager$.next(validation);
		});
	}

	displayActionOption(validator: ProductionValidator): boolean {
		switch (validator) {
			case ProductionValidator.ResponsiveInvalid:
			case ProductionValidator.ResponsiveNotResponsiveNative:
			case ProductionValidator.ResponsiveEmailFamily:
			case ProductionValidator.PrivilegeEmailFamily:
			case ProductionValidator.WorkProductEmailFamily:
				return true;
			default:
				return false;
		}
	}

	getField(validator: ProductionValidator): string {
		switch (validator) {
			case ProductionValidator.ResponsiveInvalid:
			case ProductionValidator.ResponsiveNotResponsiveNative:
			case ProductionValidator.ResponsiveEmailFamily:
				return 'Responsive'
			case ProductionValidator.PrivilegeEmailFamily:
				return 'Attorney-Client Privileged';
			case ProductionValidator.WorkProductEmailFamily:
				return 'Attorney Work Product';
			default:
				return 'Unknown';
		}
	}
}
